import React, { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import acApi from 'axios/ac/acApi';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { getTransactions } from 'store/transactions/transactionsActions';
import { getErrorMessage } from 'utils/miscUtil';
import MixpanelService from 'utils/mixpanelService';
import { RESTORE_TRANSACTION_VERSION } from 'utils/mixpanelEvents';
import { FieldDiff, TransactionVersionDiff } from './transactionVersionHistoryTypes';
import { t } from './versionHistoryUtils';
import styles from './TransactionVersionHistoryTab.module.css';

// Above this per-side line count the O(m*n) LCS matrix is skipped to keep the UI responsive.
const MAX_DIFF_LINES = 1000;

const toText = (value: any): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  return JSON.stringify(value, null, 2);
};

/**
 * Line-level LCS diff. Returns each side's lines plus a "kept" flag (true = part of the common
 * subsequence, i.e. unchanged). Lines not kept on the left were removed; not kept on the right were added.
 * For very large inputs the LCS is skipped and both sides are returned unmarked (plain, no highlight).
 */
const lineDiff = (leftText: string, rightText: string) => {
  const a = leftText.length ? leftText.split('\n') : [];
  const b = rightText.length ? rightText.split('\n') : [];
  const m = a.length;
  const n = b.length;
  if (m > MAX_DIFF_LINES || n > MAX_DIFF_LINES) {
    // Fallback: too large to diff cheaply — show both sides plain rather than freeze the tab.
    return { a, b, leftKept: new Array(m).fill(true), rightKept: new Array(n).fill(true), truncated: true };
  }
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const leftKept = new Array(m).fill(false);
  const rightKept = new Array(n).fill(false);
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      leftKept[i] = true;
      rightKept[j] = true;
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      i += 1;
    } else {
      j += 1;
    }
  }
  return { a, b, leftKept, rightKept, truncated: false };
};

const diffColumn = (lines: string[], kept: boolean[], changedClass: string) => (
  <pre className={clsx('mb-0', styles.diffPre)}>
    {lines.map((line, idx) => (
      <div key={idx} className={kept[idx] ? undefined : changedClass}>
        {line === '' ? ' ' : line}
      </div>
    ))}
  </pre>
);

const plainValue = (value: any) => {
  const text = toText(value);
  if (text === '') {
    return <span className="text-muted">—</span>;
  }
  return <pre className={clsx('mb-0', styles.diffPre)}>{text}</pre>;
};

interface Props {
  transactionId: number;
  diffVersion: number;
  headerContent: React.ReactNode;
  onBackToList: () => void;
  /** Called after a successful restore so the parent can reload the version list and switch back to it. */
  onRestored: () => void;
}

const TransactionVersionDiffView: React.FC<Props> = ({
  transactionId,
  diffVersion,
  headerContent,
  onBackToList,
  onRestored,
}) => {
  const dispatch = useDispatch();
  const [diff, setDiff] = useState<TransactionVersionDiff | null>(null);
  const [diffError, setDiffError] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDiff(null);
    setDiffError(false);
    acApi.transactions
      .getTransactionVersionDiff(transactionId, diffVersion, undefined)
      .then(response => setDiff(response.data.result))
      .catch(() => {
        setDiffError(true);
        dispatch(showToastNotification(ToastType.Danger, t('loadFailed')));
      });
  }, [transactionId, diffVersion]);

  const saveAsLatest = () => {
    setBusy(true);
    acApi.transactions
      .restoreTransactionVersion(transactionId, diffVersion)
      .then(() => {
        MixpanelService.track(RESTORE_TRANSACTION_VERSION(diffVersion));
        dispatch(showToastNotification(ToastType.Success, t('restoreSuccess', { version: diffVersion })));
        dispatch(getTransactions());
        onRestored();
      })
      .catch(e => dispatch(showToastNotification(ToastType.Danger, t('restoreFailed'), getErrorMessage(e))))
      .finally(() => setBusy(false));
  };

  return (
    <div className="bg-light p-2">
      <div className="d-flex align-items-center">
        <div>{headerContent}</div>
        <div className="ml-auto mr-4 mt-4">
          <Button variant="link" onClick={onBackToList}>
            {t('backToHistory')}
          </Button>
          <Button variant="primary" onClick={saveAsLatest} disabled={busy || !diff}>
            {busy ? <Spinner animation="border" size="sm" /> : t('saveAsLatest')}
          </Button>
        </div>
      </div>
      <div className="px-4 pt-0">
        <h5 className="mb-3">{t('restoreTitle', { version: diffVersion })}</h5>
        {diffError ? (
          <div className="text-center p-3 text-danger">{t('loadFailed')}</div>
        ) : !diff ? (
          <div className="text-center p-3">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <>
            <div className={clsx(styles.legend, 'small text-muted mb-2')}>
              <span>
                <span className={clsx(styles.legendSwatch, styles.removedLine)} />
                {t('currentValue')}
              </span>
              <span>
                <span className={clsx(styles.legendSwatch, styles.addedLine)} />
                {t('versionValue', { version: diffVersion })}
              </span>
            </div>
            <table className={clsx('table table-sm bg-white', styles.diffTable)}>
              <thead>
                <tr>
                  <th className={styles.colField}>{t('field')}</th>
                  <th>{t('currentValue')}</th>
                  <th>→ {t('versionValue', { version: diffVersion })}</th>
                </tr>
              </thead>
              <tbody>
                {diff.fields.map((f: FieldDiff) => {
                  if (!f.changed) {
                    return (
                      <tr key={f.field}>
                        <td className="font-weight-bold">{f.field}</td>
                        <td className="text-muted">{t('unchanged')}</td>
                        <td>{plainValue(f.fromValue)}</td>
                      </tr>
                    );
                  }
                  const { a, b, leftKept, rightKept, truncated } = lineDiff(toText(f.toValue), toText(f.fromValue));
                  return (
                    <tr key={f.field}>
                      <td className={clsx('font-weight-bold', styles.changedField)}>
                        {f.field}
                        <div className="text-warning small">● {t('changed')}</div>
                        {truncated && <div className="text-muted small">{t('diffTooLarge')}</div>}
                      </td>
                      <td>{diffColumn(a, leftKept, styles.removedLine)}</td>
                      <td>{diffColumn(b, rightKept, styles.addedLine)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
        <div className="text-muted small mt-2">ⓘ {t('restoreInfo')}</div>
      </div>
    </div>
  );
};

export default TransactionVersionDiffView;
